import { useEffect } from 'react'
import { gotoHome } from './room'
import { LangToggle, useI18n } from './i18n'

/**
 * このアプリの立場。**固定ページ `/about` と、画面内のダイアログで同じ内容を出す。**
 *
 * 出す義務がある/出す必要がある事柄:
 * - 地図SVG が **CC BY-NC-SA 4.0**。作者と出典の表示（BY）はライセンス上の義務
 * - **非営利・非商用**であること（NC 条項。広告・課金・寄付を一切行わない）
 * - **チート用途の全面拒否**。レイド中の現在地表示は永久に実装しない
 * - 免責（無保証・2日で消える・ルームIDを知る人は誰でも入れる）
 *
 * 文面は NOTICE.md と doc/tarkov-dev-migration/credits.md に揃える。
 * → doc/service/master.md#方針の明文化
 *
 * **ここだけは辞書に載せず、言語ごとに JSX を丸ごと切り替える**（→ doc/i18n/master.md）。
 * 段落・箇条書き・リンクの入り混じった文書で、キーに割ると訳と原文の対応が追えなくなるため。
 * **日本語が正文で、英語は参考訳**。文言を直すときは必ず両方を直す。
 */
export function AboutContent() {
  const { lang } = useI18n()
  return lang === 'ja' ? <AboutJa /> : <AboutEn />
}

/** ライセンス名・作者名・出典URL は訳さない。日英で同じものを出す */
function Credits({ lang }: { lang: 'ja' | 'en' }) {
  const ja = lang === 'ja'
  return (
    <dl className="about__list">
      <dt>{ja ? 'マップ' : 'Maps'}</dt>
      <dd>
        {ja ? 'SVG マップ: Shebuka ほか' : 'SVG maps: Shebuka and others'} —{' '}
        <a href="https://github.com/the-hideout/tarkov-dev-svg-maps" target="_blank" rel="noreferrer">
          the-hideout/tarkov-dev-svg-maps
        </a>
        <br />
        <a href="http://creativecommons.org/licenses/by-nc-sa/4.0/" target="_blank" rel="noreferrer">
          CC BY-NC-SA 4.0
        </a>
      </dd>

      <dt>{ja ? 'タスク・座標データ' : 'Task and coordinate data'}</dt>
      <dd>
        <a href="https://tarkov.dev/" target="_blank" rel="noreferrer">
          json.tarkov.dev
        </a>{' '}
        {ja
          ? '経由。ゲーム内テキストの著作権は Battlestate Games にあります'
          : '. The in-game text is copyright Battlestate Games'}
      </dd>

      <dt>{ja ? '座標変換の設定' : 'Coordinate transforms'}</dt>
      <dd>
        <a href="https://github.com/the-hideout/tarkov-dev" target="_blank" rel="noreferrer">
          the-hideout/tarkov-dev
        </a>{' '}
        (MIT)
      </dd>

      <dt>{ja ? 'このアプリのコード' : 'The code of this app'}</dt>
      <dd>MIT</dd>
    </dl>
  )
}

function AboutJa() {
  return (
    <>
      <p className="about__lead">
        Escape from Tarkov の非公式ファンプロジェクトです。Battlestate Games とは関係がありません。
      </p>

      <h3 className="about__h">非営利</h3>
      <p className="about__p">
        本サービスは<strong>非営利・非商用</strong>です。広告・課金・寄付を一切行いません。
        地図SVG が CC BY-NC-SA 4.0（NC = 非営利）の下にあるため、これは選択ではなく義務です。
        運営費は無料枠のみで賄い、枠を超える場合は新規ルームの作成を止めます（有料化はしません）。
      </p>

      <h3 className="about__h">チート用途の拒否</h3>
      <p className="about__p about__warn">
        <strong>レイド中のプレイヤー現在地をリアルタイムに表示する機能は、実装しません。</strong>
        in-game radar / ESP / 改変マップ / pixel-bot への利用および協力も一切行いません。
      </p>
      <p className="about__p">
        本アプリは<strong>手動でピンを置く作戦盤</strong>であり、ゲームから情報を取得する仕組みを持ちません。
        地図SVG のライセンスにはチート用途での自動失効条項があり、DMCA の対象と明記されています。
      </p>

      <h3 className="about__h">権利表記</h3>
      <Credits lang="ja" />

      <h3 className="about__h">免責</h3>
      <ul className="about__ul">
        <li>無料・無保証です。予告なく停止・終了することがあります</li>
        <li>
          ルームのデータ（ピン・落書き・登録タスク）は<strong>最終更新から2日で消えます。</strong>
          恒久的な保存先として使わないでください
        </li>
        <li>
          ルームIDを知っている人は誰でも入れます。<strong>秘密の情報を書かないでください</strong>
        </li>
        <li>1ルーム5人まで。1日のルーム作成数にも上限があります（無料枠を守るため）</li>
      </ul>
    </>
  )
}

function AboutEn() {
  return (
    <>
      <p className="about__lead">
        An unofficial Escape from Tarkov fan project. Not affiliated with Battlestate Games.
      </p>

      <h3 className="about__h">Non-commercial</h3>
      <p className="about__p">
        This service is <strong>non-commercial</strong>. It carries no ads and takes no payments or
        donations. The map SVGs are under CC BY-NC-SA 4.0 (NC = non-commercial), so this is an
        obligation, not a preference. It runs on free tiers only; if a limit is reached, new rooms stop
        being created (it will not go paid).
      </p>

      <h3 className="about__h">No cheating</h3>
      <p className="about__p about__warn">
        <strong>
          A feature showing player positions in real time during a raid will never be implemented.
        </strong>{' '}
        This project will not be used for, or help with, in-game radar, ESP, modified maps or pixel bots.
      </p>
      <p className="about__p">
        This app is a <strong>plan board where people place pins by hand</strong>. It has no way to read
        anything from the game. The map SVG license terminates automatically on cheating use and names
        DMCA action explicitly.
      </p>

      <h3 className="about__h">Credits</h3>
      <Credits lang="en" />

      <h3 className="about__h">Disclaimer</h3>
      <ul className="about__ul">
        <li>Free and provided as is. It may stop or shut down without notice</li>
        <li>
          Room data (pins, drawings, registered tasks) is{' '}
          <strong>deleted 2 days after its last update.</strong> Do not use it as permanent storage
        </li>
        <li>
          Anyone who knows the room ID can join. <strong>Do not write anything secret there</strong>
        </li>
        <li>Up to 5 people per room, and a daily cap on new rooms (to stay inside the free tier)</li>
      </ul>

      <p className="about__p about__note">
        The Japanese text is the authoritative version of this page; this English text is a reference
        translation.
      </p>
    </>
  )
}

/** 固定ページ `/about`。URL で指せることが要件（共有先・権利者が見に来られる） */
export function AboutPage() {
  const { t } = useI18n()
  return (
    <div className="home">
      <div className="home__card about-page">
        <div className="about__title-row">
          <h1 className="home__title">{t('about.title')}</h1>
          <LangToggle />
        </div>
        <AboutContent />
        <p className="about__p">
          <button type="button" className="prep__clear" onClick={gotoHome}>
            {t('about.back')}
          </button>
        </p>
      </div>
    </div>
  )
}

/** 画面内から開くダイアログ。ルームを離れずに読める */
export function AboutDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="about-backdrop" onClick={onClose} role="presentation">
      <div
        className="about"
        role="dialog"
        aria-modal="true"
        aria-label={t('about.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="about__head">
          <strong>Tarkov Map Pins</strong>
          <LangToggle />
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('app.close')}>
            ×
          </button>
        </div>
        <AboutContent />
        <p className="about__p">
          <a href="/about" target="_blank" rel="noreferrer">
            {t('about.openPage')}
          </a>
        </p>
      </div>
    </div>
  )
}
